/* eslint-disable react-hooks/exhaustive-deps */
import moment from 'moment';
import { Area } from '@antv/g2plot';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  filterData,
  getMaxScore,
  getLineColor,
  getYAxis,
} from './helper';
import Metrics from '../metrics';
import Widget from '../../widget';
import { addedHeaderTypes } from '../../config';
import { ContainerStyle, Style } from './style';
import { getRiskGradient } from '../../../../../screens/View/helper';
import { snakeCaseToTitleCase } from '../../../../../helpers/StringHelper';
import RiskTag from '../../../../Tags/RiskTag';

export default function RiskWidget({
  title = '',
  t = (s) => s,
  metricsItems = [],
  selectOptions = [],
  data = {},
  type = 'DEFAULT',
  loading = false,
  tooltip,
  headerLabel = "Assessed Participants",
  headerValue = "0",
}) {
  const [mainGraph, setMainGraph] = useState(null);
  const container = useRef();
  const [selectOption, setSelectOption] = useState();

  const { allData, maxScore } = useMemo(() => (
    filterData(data, selectOption)
  ), [data, selectOption]);

  const aggregatedScore = useMemo(() => {
    if (data && data[selectOption]) {
      const values = Object.values(data[selectOption]);
      return Number(values[values.length - 1]).toFixed(0);
    }

    return 0;
  }, [data, selectOption]);

  useLayoutEffect(() => {
    let roundScore = getMaxScore(type, maxScore);
    if (container.current) {
      if (mainGraph) {
        mainGraph.update({
          data: allData,
          areaStyle: () => {
            let fill = `l(270) ${getRiskGradient(roundScore)}`;
            return {
              fill,
              fillOpacity: 1,
            };
          },
          tooltip: {
            showTitle: false,
            customContent: (a, data) => {
              if (a && Array.isArray(data) && data[0] && typeof a === 'string') {
                if (a.indexOf('_') === -1) {
                  return `
                  <ul class="g2-tooltip-list" style="margin: 0px; list-style-type: none; padding: 0px;">
                    <li class="g2-tooltip-list-item" data-index="" style="list-style-type: none; padding: 0px; margin: 12px 0px;">
                      <span class="g2-tooltip-name">
                        ${snakeCaseToTitleCase(moment(a, 'M').format('MMMM'))} 
                      </span>
                    </li>
                    <li class="g2-tooltip-list-item" data-index="" style="list-style-type: none; padding: 0px; margin: 12px 0px;">
                      <span class="g2-tooltip-name">Score</span>:
                      <span class="g2-tooltip-value" style="display: inline-block; float: right; margin-left: 30px;">
                        ${Number(data[0].data.Score).toFixed(2)}
                      </span>
                    </li>
                  </ul>
                  `;
                }
              }
              return '';
            }
          },
          color: getLineColor(roundScore),
          // color: 'transparent',
        });
      } else {
        const area = new Area(container.current, {
          height: 300,
          data: allData,
          xField: 'date',
          localRefresh: true,
          tooltip: {
            showTitle: false,
            customContent: (a, data) => {
              if (a && Array.isArray(data) && data[0] && typeof a === 'string') {
                if (a.indexOf('_') === -1) {
                  return `
                  <ul class="g2-tooltip-list" style="margin: 0px; list-style-type: none; padding: 0px;">
                    <li class="g2-tooltip-list-item" data-index="" style="list-style-type: none; padding: 0px; margin: 12px 0px;">
                      <span class="g2-tooltip-name">
                        ${snakeCaseToTitleCase(moment(a, 'M').format('MMMM'))} 
                      </span>
                    </li>
                    <li class="g2-tooltip-list-item" data-index="" style="list-style-type: none; padding: 0px; margin: 12px 0px;">
                      <span class="g2-tooltip-name">Score</span>:
                      <span class="g2-tooltip-value" style="display: inline-block; float: right; margin-left: 30px;">
                        ${Number(data[0].data.Score).toFixed(2)}
                      </span>
                    </li>
                  </ul>
                  `;
                }
              }
              return '';
            }
          },
          color: getLineColor(roundScore),
          // color: 'transparent',
          animation: false,
          yField: 'Score',
          point: {
            shape: 'round',
            size: 4,
            style: (a) => {
              const score = getMaxScore(type, a.Score);
              const color = getLineColor(score);
              return {
                fill: (a.date.indexOf('_') !== -1) ? 'transparent' : 'white',
                stroke: (a.date.indexOf('_') !== -1) ? 'transparent' : color,
                lineWidth: 2,
              };
            },
          },
          xAxis: {
            label: {
              formatter: () => '',
            },
            nice: true,
            tickLine: {
              style: {
                opacity: 0,
              }
            }
          },
          yAxis: getYAxis(type),
          areaStyle: (datum) => {
            let fill = `l(270) ${getRiskGradient(roundScore)}`;
            return {
              fill,
              fillOpacity: 1,
            };
          },
        });
        area.render();
        setMainGraph(area);
      }
    }
  }, [allData]);

  const addedHeader = useMemo(() => !loading ? ({
    type: addedHeaderTypes.SELECT,
    options: selectOptions,
    label: `${t('Period')}:`,
    defaultValue: selectOptions[0] ? selectOptions[0].value : undefined,
    onChange: (a) => setSelectOption(a),
  }) : null, [t, selectOptions, loading]);

  useEffect(() => {
    if (!selectOption && Array.isArray(selectOptions) && selectOptions.length) {
      setSelectOption(selectOptions[0].value);
    }
  }, [selectOptions]);

  return (
    <Widget
      title={title}
      addedHeader={addedHeader}
      loading={loading}
      tooltip={tooltip}
      className="no-px-body"
    >
      <Style className="risk-widget-container">
        <div className="w-header">
          <div className="cont">
            <div className="flex-c jus-c">
              <label className="label">
                {t(headerLabel)}
              </label>
            </div>
            <div className="flex-c jus-c">
              <label className="value">
                {headerValue}
              </label>
            </div>
          </div>
          <div className="cont">
            <div className="flex-c jus-c">
              <label className="label">
                {t('Aggregate Risk')}
              </label>
            </div>
            <RiskTag type={type} value={aggregatedScore} t={t} />
          </div>
        </div>
        <Metrics
          items={metricsItems}
          t={t}
        />
        <ContainerStyle className={loading ? 'hidden' : ''} ref={container} />
      </Style>
    </Widget>
  );
}
